import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers";
import { addMonths, getUnixTime, subMonths } from "date-fns";
import {
  IAlarm,
  IAssetIcon,
  IBoxDevice,
  IBoxDeviceWithInstance,
  IBoxInstance,
  IBuilding,
  IBuildingGroup,
  IBuildingObject,
  IClient,
  IClientRole,
  IDeviceClass,
  IDeviceClassLoookupField,
  IDeviceFieldValue,
  IMultiCallItem,
  ISchematic,
  IUsageBuilding,
} from "@/lib/interfaces";
import { appApi } from "../../appApi";
import { encodeToHash, passGetQuery } from "../functions";
import { queryParams } from "./riskRadar";

export interface IContract {
  id: string;
  clientId: string;
  name: string;
}

export const customQueriesAPI = appApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAlarmsWithHistory: builder.query<
      { alarms: IAlarm[] },
      {
        client: string;
        building: string;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        // prep variables
        const { client, building } = _arg;
        const results: IAlarm[] = [];
        const alarms: IAlarm[] = [];
        //remapped
        const alarmsRequest = await fetchWithBQ({
          url: `/client/${client}/alarms?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "get",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/alarms?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        const alarmHistoryRequests: Array<
          MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError>>
        > = [];

        try {
          alarmsRequest.data?.data?.map((alarm: IAlarm) => {
            if (alarm.building === building) {
              alarms.push(alarm);
            }
          });
        } catch (e) {}

        alarms.map(({ id }) => {
          alarmHistoryRequests.push(
            //remapped
            fetchWithBQ({
              url: `/client/${client}/module/alarm/getAlarmHistory?data=${passGetQuery({ params: { complete: 1, alarmId: id, deviceId: 0 } })}`,
              method: "get",
              body: {
                hash: encodeToHash({
                  url: `/client/${client}/module/alarm/getAlarmHistory?data=${passGetQuery({ params: { complete: 1, alarmId: id, deviceId: 0 } })}`,
                  method: "get",
                }),
              },
            })
          );
        });

        const responses = await Promise.all(alarmHistoryRequests);
        responses.map(({ data, error, meta }) => {
          try {
            const url = meta.request.url.split("/");
            const queryString = url[9].split("=");
            const obj = JSON.parse(decodeURIComponent(queryString[1]));

            const findAlarm = alarms.find((alarm) => alarm.id === obj.alarmId);

            if (findAlarm) {
              results.push({ ...findAlarm, history: data.alarmStatusChanges });
            }
          } catch (e) {}
        });

        return {
          data: {
            alarms: results,
          },
        };
      },
    }),

    getDevicesAnnotations: builder.query<
      { devices: IBoxDeviceWithInstance[] },
      {
        client: string;
        devices: IBoxDeviceWithInstance[] | undefined;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        //prep variables
        const response: IBoxDeviceWithInstance[] = [];
        const deviceFieldValues: IDeviceFieldValue[] = [];
        const deviceClassLoookupFields: IDeviceClassLoookupField[] = [];
        const { devices } = _arg;
        const boxInstanceIds: string[] = [];
        const fieldValuesRequests: Array<
          MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError>>
        > = [];
        //get deviceFieldValues
        devices?.map((d) => {
          if (!boxInstanceIds.includes(d.instance?.id!)) {
            boxInstanceIds.push(d.instance?.id!);
          }
        });

        boxInstanceIds?.map((instanceId) => {
          //remapped
          const url = `/boxInstance/${instanceId}/deviceFieldValues?data=${passGetQuery({ params: { complete: 1 } })}`;
          fieldValuesRequests.push(
            fetchWithBQ({
              url,
              method: "GET",
              body: {
                hash: encodeToHash({
                  url,
                  method: "get",
                }),
              },
            })
          );
        });

        const responses = await Promise.all(fieldValuesRequests);
        responses.map(({ data, error, meta }) => {
          try {
            deviceFieldValues.push(...data?.data);
          } catch (e) {
            //error when API down?
          }
        });
        //remapped
        const deviceClassFieldsRequest = await fetchWithBQ({
          url: `/deviceClassFields?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "get",
          body: {
            hash: encodeToHash({
              url: `/deviceClassFields?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        try {
          if (deviceClassFieldsRequest.data.status === "success") {
            deviceClassLoookupFields.push(
              ...deviceClassFieldsRequest.data.data
            );
          }
        } catch (e) {}

        devices?.map((d) => {
          const tempDevice = { ...d };
          const annotations: IDeviceClassLoookupField[] = [];
          let annotatedAs: IDeviceClassLoookupField | undefined = undefined;

          if (d.deviceClass) {
            //device -> has sensors annotated -> MULTIPLE
            deviceClassLoookupFields.map((field) => {
              if (field.deviceClass === d.deviceClass) {
                const deviceFieldValue = deviceFieldValues.find(
                  (item) =>
                    item.device === d.id && item.deviceField === field.id
                );

                annotations.push({
                  ...field,
                  deviceValue: deviceFieldValue?.deviceValue,
                });
              }
            });
          } else {
            //sensor -> can be annotated at -> SINGLE
            const deviceFieldValue = deviceFieldValues.find(
              (item) => item.deviceValue === d.id
            );
            if (deviceFieldValue) {
              annotatedAs = deviceClassLoookupFields.find(
                (item) => item.id === deviceFieldValue.deviceField
              );
            }
          }
          response.push({ ...tempDevice, annotations, annotatedAs });
        });

        return {
          data: {
            devices: response,
          },
        };
      },
    }),

    getDevicesBy: builder.query<
      {
        devices: IBoxDeviceWithInstance[];
        device?: IBoxDeviceWithInstance;
      },
      {
        client: string;
        field: string;
        byCollectionOf: string[];
        incParent?: boolean;
        instances?: string[];
        operator?: string;
        includeSensorData?: boolean;
        dateFrom?: Date;
        dateTo?: Date;
        findOne?: boolean;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const now = new Date();
        const {
          field,
          byCollectionOf,
          incParent,
          instances,
          operator = "=",
          includeSensorData = false,
          dateFrom = subMonths(now, 1),
          dateTo = addMonths(now, 1),
          client,
          findOne = false,
        } = _arg;

        if (byCollectionOf === undefined) return { data: { devices: [] } };
        const devices: IBoxDeviceWithInstance[] = [];
        let boxInstancesList: IBoxInstance[] = [];
        const multicallRequests: Array<{ path: string; biid: string }> = [];

        if (instances) {
          boxInstancesList = instances;
        } else {
          const url = `/boxInstances?data=${passGetQuery({ params: { complete: 1, filters: [{ field: "client", value: _arg.client }] } })}`;
          const body = {
            hash: encodeToHash({ url, method: "get" }),
          };

          //remapped
          const boxInstances = await fetchWithBQ({
            url,
            body,
            method: "get",
          });

          boxInstances.data?.data?.map((boxInstance: IBoxInstance) =>
            boxInstancesList.push(boxInstance)
          );
        }

        boxInstancesList.map(({ id: biid }) => {
          multicallRequests.push({
            path: `/rest/dbo/boxInstance/${biid}/boxDevices`,
            biid,
          });
        });

        const devicesOR = byCollectionOf.map((fieldValue) => {
          return {
            field,
            value: fieldValue,
            operator,
          };
        });

        if (devicesOR.length === 0) return { data: { devices: [] } };
        //remapped
        const multicallResponse = await fetchWithBQ({
          url: `/app/concurrent`,
          method: "POST",
          body: {
            calls: multicallRequests.map(({ path, biid }) => {
              return {
                biid,
                path,
                method: "GET",
                data: {
                  complete: 1,
                  filters: { OR: devicesOR },
                },
              };
            }),
          },
        });

        multicallResponse.data?.responses?.map(
          (response: any, index: number) => {
            if (response.status === "success") {
              let boxId = 0;

              Object.keys(response.timings).map((key) => {
                if (key.includes("boxInstance")) {
                  boxId = +key.split("/")[1];
                }
              });

              const instance = boxInstancesList.find(
                (item) => item.id == boxId
              );
              response?.data?.map((d) => {
                devices.push({ ...d, instance });
              });
            }
          }
        );

        if (incParent) {
          //reqest parents and push on the same object!
          // parentItem
          const parentCalls: IMultiCallItem[] = [];

          devices.map(async (d) => {
            const parentId =
              +d?.virtualParentDevice > 0
                ? d.virtualParentDevice
                : d.parentItem;

            const checkParentId = parentCalls.find(
              (p) => p.data.filters[0].value == parentId
            );
            if (checkParentId) return;

            parentCalls.push({
              biid: d.instance?.id,
              method: "GET",
              path: `/rest/dbo/boxInstance/${d.instance?.id}/boxDevices`,
              data: {
                complete: 1,
                filters: [{ field: "id", value: parentId }],
              },
            });
          });
          //remapped
          const parentMulticallResponse = await fetchWithBQ({
            url: `/app/concurrent`,
            method: "POST",
            body: {
              calls: parentCalls,
            },
          });

          const parentDevices = [];
          parentMulticallResponse?.data?.responses?.map((t: any) => {
            t?.data?.map((d: any) => {
              parentDevices.push(d);
            });
          });

          // add the parent devices to the devices
          devices.map((d) => {
            const parentId =
              +d?.virtualParentDevice > 0
                ? d.virtualParentDevice
                : d.parentItem;

            const parentDevice = parentDevices.find((pd) => pd.id === parentId);
            if (parentDevice) {
              d.parent = parentDevice;
            }
          });
        }

        if (includeSensorData && dateFrom && dateTo) {
          const calls: IMultiCallItem[] = [];
          const devicesWithData = [];

          devices?.map(async (d) => {
            const data = {
              boxInstance: d.instance?.id,
              sensors: [d.id],
              from: getUnixTime(dateFrom),
              to: getUnixTime(dateTo),
            };

            const path = `/rest/dbo/client/${client}/module/sensor/getTraces`;

            calls.push({
              path,
              method: "GET",
              data,
            });
          });

          const multicallResponse = await fetchWithBQ({
            url: `/app/concurrent`,
            method: "POST",
            body: { calls },
          });

          devices?.map(async (d) => {
            const findReading = multicallResponse?.data?.responses?.find((r) =>
              r.sensors?.includes(d.id)
            );

            const readings =
              findReading?.readings?.length > 0 ? findReading.readings[0] : [];

            const oneDayTimestamp = 24 * 60 * 60;

            const newReadings = readings?.map((r) => {
              const newTime = +r.time - 3 * oneDayTimestamp;

              const newDate = new Date(newTime * 1000);
              //zero hours, minutes, seconds
              newDate.setHours(12, 0, 0, 0);

              return { ...r, time: getUnixTime(newDate) };
            });

            devicesWithData.push({ ...d, readings: newReadings });
          });

          return {
            data: {
              devices: devicesWithData,
            },
          };
        }

        return {
          data: {
            devices,
            device: findOne && devices.length > 0 ? { ...devices[0] } : devices,
          },
        };
      },
    }),

    getDeviceByField: builder.query<
      { devices: IBoxDevice[] },
      {
        client: string;
        incParent: boolean;
        field: string;
        value: string;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { value, field } = _arg;

        const devices: IBoxDevice[] = [];
        const requests: Array<
          MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError, {}>>
        > = [];

        //remapped
        const boxInstances = await fetchWithBQ({
          url: `/boxInstances?data=${passGetQuery({ params: { complete: 1, filters: [{ field: "client", value: _arg.client }] } })}`,
          method: "get",
          body: {
            hash: encodeToHash({
              url: `/boxInstances?data=${passGetQuery({ params: { complete: 1, filters: [{ field: "client", value: _arg.client }] } })}`,
              method: "get",
            }),
          },
        });

        boxInstances?.data?.data?.map((boxInstance) => {
          requests.push(
            //remapped
            fetchWithBQ({
              url: `/boxInstance/${boxInstance.id}/boxDevices?data=${passGetQuery({ params: { complete: 1, filters: [{ field, value }] } })}`,
              method: "get",
              body: {
                hash: encodeToHash({
                  url: `/boxInstance/${boxInstance.id}/boxDevices?data=${passGetQuery({ params: { complete: 1, filters: [{ field, value }] } })}`,
                  method: "get",
                }),
              },
            })
          );
        });

        const responses = await Promise.all(requests);

        responses.map((response) => {
          if (response?.data?.data?.length > 0) {
            response?.data?.data?.map((d) => devices.push(d));
          }
        });

        return {
          data: {
            devices: devices.length > 0 ? devices : null,
          },
        };
      },
    }),

    getClientContracts: builder.query<
      { contracts: Array<IContract> },
      Array<string>
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const contracts: Array<IContract> = [];
        const calls: IMultiCallItem[] = [];

        _arg.map((clientId) => {
          calls.push({
            path: `/rest/dbo/client/${clientId}/contracts`,
            method: "GET",
            data: { complete: "1" },
          });
        });

        if (calls.length === 0) return { data: { buildings: [] } };

        //remapped
        const multicallResponse = await fetchWithBQ({
          url: `/app/concurrent`,
          method: "POST",
          body: { calls },
        });

        multicallResponse.data?.responses?.map(
          (response: any, index: number) => {
            if (response.status === "success") {
              try {
                const clientId = _arg[index];
                response?.data?.map((contract) => {
                  contracts.push({
                    id: contract.id,
                    name: contract.name,
                    clientId,
                  });
                });
              } catch (e) {}
            }
          }
        );

        return {
          data: {
            contracts,
          },
        };
      },
    }),

    getClientBuildings: builder.query<
      { buildings: Array<IBuildingObject> },
      Array<IClient>
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const buildings: Array<IBuildingObject> = [];
        const calls: IMultiCallItem[] = [];

        _arg.map(({ id }) => {
          calls.push({
            path: `/rest/dbo/client/${id}/buildings`,
            method: "GET",
            data: { complete: "1" },
          });
        });

        if (calls.length === 0) return { data: { buildings: [] } };

        //remapped
        const multicallResponse = await fetchWithBQ({
          url: `/app/concurrent`,
          method: "POST",
          body: { calls },
        });

        multicallResponse.data?.responses?.map(
          (response: any, index: number) => {
            if (response.status === "success") {
              try {
                const client = _arg[index];
                response?.data?.map((b) => {
                  buildings.push({ ...b, clientId: client.id });
                });
              } catch (e) {}
            }
          }
        );

        return {
          data: {
            buildings,
          },
        };
      },
    }),

    getClientRoles: builder.query<
      { roles: Array<IClientRole> },
      Array<IClient>
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const roles: Array<IClientRole> = [];
        const calls: IMultiCallItem[] = [];

        _arg.map(({ id }) => {
          calls.push({
            path: `/rest/dbo/client/${id}/clientRoles`,
            method: "GET",
            data: { complete: "1" },
          });
        });

        if (calls.length === 0) return { data: { roles: [] } };
        //remapped
        const multicallResponse = await fetchWithBQ({
          url: `/app/concurrent`,
          method: "POST",
          body: { calls },
        });

        multicallResponse.data?.responses?.map(
          (response: any, index: number) => {
            if (response.status === "success") {
              try {
                const client = _arg[index];
                response?.data?.map((role) => {
                  roles.push({ ...role, client: client.id });
                });
              } catch (e) {}
            }
          }
        );

        return {
          data: {
            roles,
          },
        };
      },
    }),

    getInductionHistory: builder.query<
      { history: any[] },
      {
        client: string;
        buildings: IBuilding[];
        sites: IBuildingGroup[];
        user: string;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { client, buildings, sites, user } = _arg;
        const inductionInvitationsIds: any[] = [];
        const inductionIds: any[] = [];
        const history: any[] = [];

        //GET HISTORY
        const fetchHistoryFilters = {
          complete: "1",
          filters: [
            { field: "annualHS", value: "1" },
            { field: "result", value: "passed" },
            { field: "user", value: user },
          ],
        };

        //remapped
        const { data: fetchHistory } = await fetchWithBQ({
          url: `/client/${client}/inductionHistories?data=${queryParams({ filters: fetchHistoryFilters })}`,
          method: "get",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/inductionHistories?data=${queryParams({ filters: fetchHistoryFilters })}`,
              method: "get",
            }),
          },
        });

        fetchHistory?.data?.map((h) => {
          if (inductionInvitationsIds.includes(h.inductionInvitation)) return;
          inductionInvitationsIds.push(h.inductionInvitation);
        });

        //GET INDUCTION INVITATIONS - BASED ON HISTORY
        const fetchInvitationsFilters = {
          complete: "1",
          filters: [{ field: "user", value: user }],
        };

        //remapped
        const { data: fetchInvitations } = await fetchWithBQ({
          url: `/client/${client}/inductionInvitations?data=${queryParams({ filters: fetchInvitationsFilters })}`,
          method: "get",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/inductionInvitations?data=${queryParams({ filters: fetchInvitationsFilters })}`,
              method: "get",
            }),
          },
        });

        fetchInvitations?.data?.map((h) => {
          if (inductionIds.includes(h.induction)) return;
          inductionIds.push(h.induction);
        });

        //GET INDUCTIONS - BASED ON INVITATIONS

        const fetchInductionsFilters = {
          complete: "1",
        };

        //remapped
        const { data: fetchInducitons } = await fetchWithBQ({
          url: `/client/${client}/inductions?data=${queryParams({ filters: fetchInductionsFilters })}`,
          method: "GET",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/inductions?data=${queryParams({ filters: fetchInductionsFilters })}`,
              method: "get",
            }),
          },
        });

        fetchHistory?.data?.map((h) => {
          const inductionInvitation = fetchInvitations?.data?.find(
            (i) => i.id === h.inductionInvitation
          );
          const induction = fetchInducitons?.data?.find(
            (i) => i.id === inductionInvitation?.induction
          );

          const tempSite = sites.find((s) => s.id === induction?.site);
          const building = buildings.find((b) => b.id === induction?.building);

          const siteBuildings: string[] = [];
          try {
            JSON.parse(tempSite?.buildings).map((b: string) =>
              siteBuildings.push(b)
            );
          } catch (e) {}
          const site = tempSite
            ? { ...tempSite, buildings: siteBuildings }
            : undefined;

          history.push({
            ...h,
            site,
            building,
            inductionInvitation,
            induction,
          });
        });

        return {
          data: {
            history,
          },
        };
      },
    }),

    //

    getDeviceFieldValues: builder.query<
      { devices: IBoxDeviceWithInstance[] },
      {
        client: string;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const fieldValuesRequests: Array<
          MaybePromise<QueryReturnValue<unknown, FetchBaseQueryError>>
        > = [];

        const { client } = _arg;
        const boxInstancesIds = [];
        // TODO
        const { data: boxInstancesData } = await fetchWithBQ(
          `/client/${client}/boxInstances?data=${passGetQuery({ params: { complete: 1 } })}`
        );
        boxInstancesData?.data?.map((bi) => {
          boxInstancesIds.push(bi.id);
        });
        boxInstancesIds?.map((id) => {
          //remapped
          fieldValuesRequests.push(
            fetchWithBQ({
              url: `/boxInstance/${id}/deviceFieldValues?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
              body: {
                hash: encodeToHash({
                  url: `/boxInstance/${id}/deviceFieldValues?data=${passGetQuery({ params: { complete: 1 } })}`,
                  method: "get",
                }),
              },
            })
          );
        });

        const fieldValues = [];

        const responses = await Promise.all(fieldValuesRequests);

        responses.map(({ data, error, meta }) => {
          fieldValues.push(...data?.data);
        });

        return {
          data: {
            data: fieldValues,
          },
        };
      },
    }),

    getAssetsIcon: builder.query<
      { icons: IAssetIcon[] },
      {
        client: string;
        building: string;
        assetIds: string[];
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { client, building, assetIds } = _arg;

        const response: any[] = [];

        //1. remapped
        const { data: assetsData } = await fetchWithBQ({
          url: `/client/${client}/building/${building}/assets?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "GET",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/building/${building}/assets?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        //2. remapped
        const { data: categoryAssetsData } = await fetchWithBQ({
          url: `/client/${client}/categoryAssets?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "GET",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/categoryAssets?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        //3. remapped
        const { data: iconsData } = await fetchWithBQ({
          url: `/deviceClassIcons?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "GET",
          body: {
            hash: encodeToHash({
              url: `/deviceClassIcons?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        //how to await for map to finish?
        await Promise.all(
          assetIds.map(async (assetId) => {
            const findAsset = assetsData?.data.find(
              (asset) => asset.id == assetId
            );
            if (findAsset) {
              const categoryId = +findAsset.categoryId;
              if (categoryId > 0) {
                const findCategoryAsset = categoryAssetsData?.data.find(
                  (asset) => asset.id == categoryId
                );
                if (findCategoryAsset) {
                  const iconId = +findCategoryAsset.icon;
                  if (iconId > 0) {
                    const findIcon = iconsData?.data.find(
                      (icon) => icon.id == iconId
                    );

                    if (findIcon) {
                      //remapped
                      const { data: iconData } = await fetchWithBQ({
                        url: `/file/${findIcon.fileHash}`,
                        method: "GET",
                        body: {
                          hash: encodeToHash({
                            url: `/file/${findIcon.fileHash}`,
                            method: "get",
                          }),
                        },
                      });

                      response.push({
                        icon: iconData?.data,
                        assetId,
                      });
                    }
                  }
                }
              }
            }
          })
        );

        return {
          data: {
            icons: response,
          },
        };
      },
    }),
    //Device classes with icons

    getDeviceClasses: builder.query<
      { deviceClasses: IDeviceClass[] },
      {
        client: string;
        includeIcons?: boolean;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { client, includeIcons = false } = _arg;

        const deviceClasses: IDeviceClass[] = [];

        //remapped
        const { data: deviceClassesData } = await fetchWithBQ({
          url: `/client/${client}/deviceClasses?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "GET",
          body: {
            hash: encodeToHash({
              url: `/client/${client}/deviceClasses?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        const { data: iconsData } = await fetchWithBQ({
          url: `/deviceClassIcons?data=${passGetQuery({ params: { complete: 1 } })}`,
          method: "GET",
          body: {
            hash: encodeToHash({
              url: `/deviceClassIcons?data=${passGetQuery({ params: { complete: 1 } })}`,
              method: "get",
            }),
          },
        });

        if (includeIcons) {
          await Promise.all(
            deviceClassesData?.data.map(async (deviceClass) => {
              const iconId = +deviceClass.icon;
              if (iconId > 0) {
                const findIcon = iconsData?.data.find(
                  (icon) => icon.id == iconId
                );

                if (findIcon) {
                  //remapped
                  const { data: iconData } = await fetchWithBQ({
                    url: `/file/${findIcon.fileHash}`,
                    method: "GET",
                    body: {
                      hash: encodeToHash({
                        url: `/file/${findIcon.fileHash}`,
                        method: "get",
                      }),
                    },
                  });

                  deviceClasses.push({
                    ...deviceClass,
                    iconObject: iconData?.data,
                  });
                } else {
                  //icon not found
                  deviceClasses.push(deviceClass);
                }
              }
            })
          );
        } else {
          deviceClasses.push(...deviceClassesData?.data);
        }

        return {
          data: {
            deviceClasses,
          },
        };
      },
    }),

    //get cafm tasks with multiple buildings
    getCafmTasks: builder.query<
      { cafmTasks: any },
      {
        client: string;
        buildings: string[];
        fromDate: number;
        toDate: number;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const cafmTasks: Record<string, any> = {};
        const { client, buildings, fromDate, toDate } = _arg;

        await Promise.all(
          buildings.map(async (building) => {
            const url = `/client/${client}/cafm/tasks?data=${passGetQuery({
              params: {
                building,
                period: { from: fromDate, to: toDate },
              },
            })}`;
            //remapped
            const { data } = await fetchWithBQ({
              url,
              method: "GET",
              body: {
                hash: encodeToHash({
                  url,
                  method: "get",
                }),
              },
            });

            cafmTasks[building] = data?.data;
          })
        );

        return {
          data: {
            cafmTasks,
          },
        };
      },
    }),

    getUsageByBuildings: builder.query<
      { usage: IUsageBuilding[] },
      {
        client: string;
        buildings: string[];
        dateFrom: number;
        dateTo: number;
        utility: string;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const usage: IUsageBuilding[] = [];
        const { client, buildings, dateFrom, dateTo, utility } = _arg;
        await Promise.all(
          buildings.map(async (building) => {
            const url = `/client/${client}/module/sensor/energyUseOverTime?data=${passGetQuery(
              {
                params: {
                  building,
                  utility,
                  dateFrom,
                  dateTo,
                },
              }
            )}`;

            const body = {
              hash: encodeToHash({ url, method: "get" }),
            };

            //remapped
            const { data } = await fetchWithBQ({
              url,
              body,
              method: "POST",
            });

            usage.unshift({
              building,
              costByDate: data?.costByDate,
              usageByDate: data?.usageByDate,
            });
          })
        );

        return {
          data: {
            usage,
          },
        };
      },
    }),

    //get images by ids
    getImagesByIds: builder.query<
      { icons: ISchematic[] },
      {
        schematicIds: string[];
        client?: string;
      }
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { schematicIds, client } = _arg;
        const response: ISchematic[] = [];
        let prefixUrl = client ? `/client/${client}` : "";
        await Promise.all(
          schematicIds.map(async (sid) => {
            //remapped
            const { data: iconData } = await fetchWithBQ({
              url: `${prefixUrl}/file/${sid}`,
              method: "GET",
              body: {
                hash: encodeToHash({
                  url: `${prefixUrl}/file/${sid}`,
                  method: "get",
                }),
              },
            });

            if (iconData.status === "success") {
              response.push({
                schematic: iconData?.data?.content as string,
                id: sid,
              });
            } else {
            }
          })
        );

        return {
          data: {
            icons: response,
          },
        };
      },
    }),
  }),
});

export const {
  useGetInductionHistoryQuery,
  useLazyGetInductionHistoryQuery,
  useGetClientContractsQuery,
  useGetClientBuildingsQuery,
  useGetClientRolesQuery,
  useGetDevicesByQuery,
  useLazyGetDevicesByQuery,
  useGetDevicesAnnotationsQuery,
  useGetAlarmsWithHistoryQuery,
  useLazyGetDevicesAnnotationsQuery,
  useGetDeviceByFieldQuery,
  useGetDeviceFieldValuesQuery,
  useGetAssetsIconQuery,
  useGetDeviceClassesQuery,
  useGetCafmTasksQuery,
  useGetUsageByBuildingsQuery,
  useGetImagesByIdsQuery,
} = customQueriesAPI;
